import React, { useCallback, useMemo, useState } from "react";
import styles from "../styles/Guide.module.scss";
import GuideSidebar from "../components/guide/GuideSidebar";
import MobileSidebar from "../components/guide/MobileSidebar";
import AlertCenter from "./AlertCenter";
import { useMediaQuery } from "@mui/material";
import MobileLayout from "./MobileLayout";
import {
  DASHBOARD,
  ENHANCE_DASHBOARD,
  ENHANCE_QUIZ,
  GUIDES_CAREGIVER,
  GUIDES_ENACT,
  GUIDES_ENHANCE,
  GUIDES_ENTRUST,
  GUIDES_EXPLORE,
  GUIDES_PATIENT,
  GUIDES_EQUIP,
  GUIDES_ENSURE,
  MY_DOCUMENTS,
  PROFILE_BILLING,
  PROFILE_GENERAL,
  PROFILE_NOTIFICATIONS,
  SHARED_DOCUMENTS,
  PWG_PRODUCTS,
} from "../constants/routes";
import { useAuth } from "../firebase/useAuth";
import usePrepFqProfile from "../views/prepFq/fetchers/usePrepFqProfile";

export interface MenuItem {
  type: string;
  title: string;
  icon: string;
  link?: string;
  relative?: boolean;
  links?: {
    multi?: boolean;
    subLinks?: {
      title: string;
      relative?: boolean;
      link: string;
      multtGuide?: any;
    }[];
    relative?: boolean;
    title: string;
    link?: string;
  }[];
}

interface Props {
  bodyClass?: string;
  blockClass?: string;
  contentClass?: string;
  hideFooter?: boolean;
  children?: React.ReactNode;
}

const PrimaryLayout: React.FC<Props> = ({
  children,
  bodyClass,
  blockClass,
  contentClass,
  hideFooter,
}) => {
  const matches = useMediaQuery("(max-width:950px)");
  const [openSidebar, setOpenSidebar] = useState(false);
  const [closeSidebar, setCloseSidebar] = useState(false);

  const authUser = useAuth();
  const { profile: prepFqProfile } = usePrepFqProfile(authUser?.user?.uid);

  const getEnhanceLink = useCallback(() => {
    if (prepFqProfile?.prep_fq_status) {
      if (prepFqProfile?.prep_fq_status === "not_started") {
        return `${GUIDES_ENHANCE}`;
      } else if (prepFqProfile?.prep_fq_status === "in_progress") {
        return `${ENHANCE_QUIZ}/${prepFqProfile.current_page}`;
      } else if (prepFqProfile?.prep_fq_status === "completed") {
        return `${ENHANCE_DASHBOARD}`;
      }
    } else {
      return "";
    }
  }, [prepFqProfile?.prep_fq_status, prepFqProfile?.current_page]);

  const primaryMenu: MenuItem[] = useMemo(() => {
    return [
      {
        type: "link",
        relative: true,
        title: "Home",
        icon: "home",
        link: DASHBOARD,
      },
      {
        type: "accordion",
        title: "Documents",
        icon: "docu",
        links: [
          { title: "My Documents", relative: true, link: MY_DOCUMENTS },
          { title: "Shared Documents", relative: true, link: SHARED_DOCUMENTS },
          {
            title: "How to Update Your Documents",
            relative: false,
            link: "https://planwellguide.com/frequently-asked-questions/#update",
          },
        ],
      },
      {
        type: "accordion",
        title: "Guides",
        icon: "edit",
        links: [
          {
            multi: true,
            title: "Life Planning",
            subLinks: [
              {
                title: "Guide to Enhancing",
                relative: true,
                link: getEnhanceLink(),
                baseLink: GUIDES_ENHANCE,
              },
            ],
          },
          {
            multi: true,
            title: "ASIP Guides",
            subLinks: [
              {
                title: "Guide to Exploring",
                relative: true,
                link: GUIDES_EXPLORE,
              },
              {
                title: "Guide to Equipping",
                relative: true,
                link: GUIDES_EQUIP,
              },
              {
                title: "Guide to Entrusting",
                relative: true,
                link: GUIDES_ENTRUST,
              },
            ],
          },
          {
            multi: true,
            title: "Other Medical Guides",
            multiGuide: [
              {
                type: "accordion",
                multi: true,
                title: "Empowering",
                links: [
                  {
                    title: "Caregiver",
                    relative: true,
                    link: GUIDES_CAREGIVER,
                  },
                  { title: "Patient", relative: true, link: GUIDES_PATIENT },
                ],
              },
            ],
            subLinks: [
              { title: "Enacting", relative: true, link: GUIDES_ENACT },
              { title: "Ensuring", relative: true, link: GUIDES_ENSURE },
            ],
          },
        ],
      },
      {
        type: "link",
        relative: true,
        title: "Products",
        icon: "product",
        link: PWG_PRODUCTS,
      },
      {
        type: "accordion",
        title: "My Account",
        icon: "account",
        links: [
          { title: "Profile", relative: true, link: PROFILE_GENERAL },
          { title: "Billing", relative: true, link: PROFILE_BILLING },
          {
            title: "Notifications",
            relative: true,
            link: PROFILE_NOTIFICATIONS,
          },
        ],
      },
      {
        type: "accordion",
        title: "Get Help",
        icon: "help",
        links: [
          {
            title: "Navigators",
            relative: false,
            link: "https://planwellguide.com/navigators/ ",
          },
          {
            title: "Learning Center",
            relative: false,
            link: "https://planwellguide.com/learn/",
          },
          {
            title: "Resources",
            relative: false,
            link: "https://planwellguide.com/resources/",
          },
          {
            title: "FAQs",
            relative: false,
            link: "https://planwellguide.com/frequently-asked-questions/",
          },
          { title: "Contact Us", relative: true, link: "/contact" },
          {
            title: "How to Update Your Documents",
            relative: false,
            link: "https://planwellguide.com/frequently-asked-questions/#update",
          },
        ],
      },
    ];
  }, [getEnhanceLink]);

  const closeNav = () => {
    setOpenSidebar(true);
    setCloseSidebar(true);
  };

  return (
    <div className={styles.root}>
      {matches ? (
        <>
          <MobileLayout
            openSidebar={openSidebar}
            closeSidebar={closeSidebar}
            setOpenSidebar={setOpenSidebar}
            setCloseSidebar={setCloseSidebar}
            closeNav={closeNav}
          />
          {openSidebar ? <MobileSidebar menu={primaryMenu} /> : ""}
        </>
      ) : (
        <GuideSidebar menu={primaryMenu} />
      )}
      <></>
      <div className={contentClass ? contentClass : styles.content}>
        <div className={blockClass ? blockClass : styles.block}>
          <div className={bodyClass ? bodyClass : styles.body}>{children}</div>
          <AlertCenter />
        </div>
        {!hideFooter && (
          <footer className={styles.footer}>
            <div>® Plan Well Guide, {new Date().getFullYear()}</div>
            <div>
              <ul>
                <li>
                  <a href="https://www.facebook.com/planwellguide/">
                    <img src={"/public-new/footer/facebook.svg"} alt="" />
                  </a>
                </li>
                <li>
                  <a href="https://twitter.com/plan_well_guide">
                    <img src={"/public-new/footer/x.svg"} alt="" />
                  </a>
                </li>
                <li>
                  <a href="https://www.instagram.com/plan_well_guide/">
                    <img src={"/public-new/footer/instagram.svg"} alt="" />
                  </a>
                </li>
                <li>
                  <a href="https://www.linkedin.com/company/plan-well-guide/">
                    <img src={"/public-new/footer/linkedin.svg"} alt="" />
                  </a>
                </li>
              </ul>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default PrimaryLayout;
